/**
 * Session assessment draft state — pure helpers for multi-row edit + batch save.
 * Used by EditAssessmentPilot / EditAssessmentInstructor (web/mobile parity).
 *
 * @see docs/architecture/web-session-assessment-batch-save.md
 */

import type { BatchAssessmentUpdate } from './assessment-versions';

/** Minimum row shape loaded from assessments_pilot / assessments_instructor. */
export interface SessionAssessmentRow {
  id: string;
  score: number | null;
  analysis: string | null;
  goals: string | null;
  evidence: unknown;
}

/** Per-row overrides while editing (only set fields are sent in batch COALESCE updates). */
export interface AssessmentRowDraft {
  score?: number | null;
  analysis?: string | null;
  goals?: string | null;
  evidence?: unknown;
  /** When true, evidence is included in batch even if unchanged vs baseline. */
  evidenceTouched?: boolean;
}

export type DraftMap = Record<string, AssessmentRowDraft>;

export function createEmptyDraftMap(): DraftMap {
  return {};
}

export function createEmptyDirtySet(): Set<string> {
  return new Set();
}

/** Filter pilot rows for the active crew role (null pilot_role rows stay visible). */
export function filterAssessmentsForPilotRole<T extends { pilot_role?: string | null }>(
  rows: T[],
  role: string | null,
): T[] {
  if (!role) return rows;
  return rows.filter((a) => a.pilot_role === role || a.pilot_role == null);
}

/** Merge a partial patch into the draft for a row and return the updated map. */
export function applyDraftPatch(
  drafts: DraftMap,
  assessmentId: string,
  patch: AssessmentRowDraft,
): DraftMap {
  return {
    ...drafts,
    [assessmentId]: {
      ...drafts[assessmentId],
      ...patch,
    },
  };
}

/** Full-row flush (e.g. before switching competency) — marks all editable fields. */
export function draftFromRow<T extends SessionAssessmentRow>(row: T): AssessmentRowDraft {
  return {
    score: row.score,
    analysis: row.analysis,
    goals: row.goals,
    evidence: row.evidence,
    evidenceTouched: false,
  };
}

/**
 * Effective row for UI binding: baseline merged with draft overrides.
 */
export function getEffectiveRow<T extends SessionAssessmentRow>(
  baseline: T,
  drafts: DraftMap,
): T {
  const draft = drafts[baseline.id];
  if (!draft) return baseline;

  return {
    ...baseline,
    ...(draft.score !== undefined ? { score: draft.score } : {}),
    ...(draft.analysis !== undefined ? { analysis: draft.analysis } : {}),
    ...(draft.goals !== undefined ? { goals: draft.goals } : {}),
    ...(draft.evidence !== undefined ? { evidence: draft.evidence } : {}),
  } as T;
}

export function isDirtySet(dirtyIds: Set<string>): boolean {
  return dirtyIds.size > 0;
}

export function getDirtyCount(dirtyIds: Set<string>): number {
  return dirtyIds.size;
}

/**
 * Build RPC batch payload for dirty rows only.
 * NULL fields → COALESCE in create_assessment_version (keep existing DB value).
 */
export function buildBatchUpdates<T extends SessionAssessmentRow>(
  baselineRows: T[],
  drafts: DraftMap,
  dirtyIds: Set<string>,
): BatchAssessmentUpdate[] {
  const byId = new Map(baselineRows.map((r) => [r.id, r]));

  return [...dirtyIds]
    .map((id) => {
      const baseline = byId.get(id);
      const draft = drafts[id];
      if (!baseline || !draft) return null;

      const update: BatchAssessmentUpdate = { assessmentId: id };

      if (draft.score !== undefined) {
        update.score = draft.score;
      }
      if (draft.analysis !== undefined) {
        update.analysis = draft.analysis;
      }
      if (draft.goals !== undefined) {
        update.goals = draft.goals;
      }
      // Only send evidence when explicitly edited — never on score/analysis/goals-only saves.
      if (draft.evidenceTouched) {
        update.evidence =
          draft.evidence !== undefined ? draft.evidence : baseline.evidence;
      }

      return update;
    })
    .filter((u): u is BatchAssessmentUpdate => u !== null);
}

/**
 * Apply saved rows from DB after batch save — returns cleared draft state.
 */
export function clearDraftState(): { drafts: DraftMap; dirtyIds: Set<string> } {
  return { drafts: createEmptyDraftMap(), dirtyIds: createEmptyDirtySet() };
}

/**
 * Detect whether a draft differs from baseline (for optional UI hints).
 */
/** Save button label for session batch editors (Phase 4.1). */
export function getSessionSaveButtonLabel(isSaving: boolean, dirtyCount: number): string {
  if (isSaving) return 'Saving...';
  if (dirtyCount > 1) return `Save all changes (${dirtyCount})`;
  return 'Save';
}

export function rowDiffersFromBaseline<T extends SessionAssessmentRow>(
  baseline: T,
  draft: AssessmentRowDraft | undefined,
): boolean {
  if (!draft) return false;

  if (draft.score !== undefined && draft.score !== baseline.score) return true;
  if (draft.analysis !== undefined && draft.analysis !== baseline.analysis) return true;
  if (draft.goals !== undefined && draft.goals !== baseline.goals) return true;
  if (draft.evidenceTouched || draft.evidence !== undefined) {
    if (draft.evidence !== undefined && draft.evidence !== baseline.evidence) return true;
    if (draft.evidenceTouched) return true;
  }
  return false;
}
